<script>
    import httpService from '@/services/core/http.service';
    import utilsService from '@/services/core/utils.service';
    import Spinner from '@/components/shared/Spinner.vue';

    // Twilio's own status strings. "Sent" is not proof of receipt — only "delivered" is.
    const STATUSES = {
        delivered: { css: 'text-bg-success', text: 'Delivered' },
        sent: { css: 'text-bg-info', text: 'Sent' },
        sending: { css: 'text-bg-info', text: 'Sending' },
        queued: { css: 'text-bg-info', text: 'Queued' },
        accepted: { css: 'text-bg-info', text: 'Accepted' },
        scheduled: { css: 'text-bg-info', text: 'Scheduled' },
        undelivered: { css: 'text-bg-danger', text: 'Not delivered' },
        failed: { css: 'text-bg-danger', text: 'Failed' },
        received: { css: 'text-bg-secondary', text: 'Received' },
        canceled: { css: 'text-bg-dark', text: 'Canceled' }
    };

    const FAILURE_REASONS = {
        21211: 'Twilio could not read this number. Check the number on the account.',
        21610: 'They replied STOP. Texts resume only if they reply START from that phone.',
        30003: 'Their phone was unreachable — switched off, or out of coverage.',
        30005: 'That number is unknown or no longer in service.',
        30006: 'That number is a landline and cannot receive texts.',
        30007: 'Their carrier filtered the message.'
    };

    const ALERT_OPENER = /^greasebook alert$/i;
    const URL_IN_TEXT = /https?:\/\/\S+/;

    export default {
        components: {
            Spinner
        },
        data() {
            return {
                phone: this.$route.query.phone || '',
                result: null,
                searched: false,
                isLoading: false
            };
        },
        methods: {
            search() {
                if (!this.phone.trim()) {
                    return;
                }

                this.isLoading = true;
                httpService
                    .get('sms-subscriptions/lookup', { phone: this.phone })
                    .then(result => {
                        this.result = result;
                        this.searched = true;
                    })
                    .catch(e => {
                        this.result = null;
                        this.searched = true;
                        utilsService.showToast(e?.data?.message || 'That number could not be looked up.', 'error');
                    })
                    .finally(() => (this.isLoading = false));
            },
            isInbound(message) {
                return (message.direction || '').indexOf('inbound') === 0;
            },
            getStatus(message) {
                return STATUSES[(message.status || '').toLowerCase()] || { css: 'text-bg-light', text: message.status || 'Unknown' };
            },
            getFailureReason(message) {
                return FAILURE_REASONS[message.errorCode] || message.errorMessage;
            },
            // Presentation only — the row keeps the exact text that was sent, in its tooltip.
            parseBody(message) {
                const lines = String(message.body || '')
                    .split('\n')
                    .map(line => line.trim())
                    .filter(Boolean);

                if (lines.length > 1 && ALERT_OPENER.test(lines[0])) {
                    lines.shift();
                }

                const url = (String(message.body || '').match(URL_IN_TEXT) || [])[0] || null;
                // Whole line: what precedes the URL is a label introducing it, left dangling otherwise.
                const withoutUrl = lines.filter(line => !URL_IN_TEXT.test(line));

                return {
                    subject: withoutUrl[0] || (url ? '' : '-'),
                    detail: withoutUrl.slice(1).join(' '),
                    url
                };
            },
            formatDate(value) {
                return utilsService.dateFormat(value, 'MMM DD, YYYY hh:mm A') || '-';
            }
        },
        mounted() {
            if (this.phone) {
                this.search();
            }
        }
    };
</script>

<template>
    <div class="main-content-header">
        <h3>
            <i class="bi bi-search"></i>
            SMS Lookup
        </h3>
    </div>
    <div class="main-content-body">
        <form class="row g-2 align-items-center mb-4" @submit.prevent="search">
            <div class="col-auto">
                <label class="col-form-label" for="sms-lookup-phone">Phone number</label>
            </div>
            <div class="col-auto">
                <input
                    id="sms-lookup-phone"
                    v-model="phone"
                    type="search"
                    class="form-control"
                    style="min-width: 22rem"
                    placeholder="(555) 123-4567, 5551234567 or +15551234567"
                    autofocus
                />
            </div>
            <div class="col-auto">
                <button type="submit" class="btn btn-primary" :disabled="!phone.trim() || isLoading">Look up</button>
            </div>
            <div class="col-12">
                <small class="text-muted">Any format — punctuation and a leading 1 or +1 are all the same number.</small>
            </div>
        </form>

        <div v-if="searched && result">
            <div class="d-flex flex-wrap align-items-center gap-3 mb-3">
                <h4 class="mb-0">{{ result.phoneNumber }}</h4>
                <span class="badge" :class="result.subscriptionStatus === 'Unsubscribed' ? 'text-bg-danger' : 'text-bg-success'">
                    {{ result.subscriptionStatus }}
                </span>
                <small v-if="result.unsubscribedAt" class="text-muted">since {{ formatDate(result.unsubscribedAt) }}</small>
            </div>

            <!-- A number is not a tenant: the same one is often on file for several operators, and
                 this list is what tells you whose traffic the messages below are. -->
            <div class="mb-4">
                <h6 class="text-muted">On file for</h6>
                <ul v-if="result.accounts.length" class="list-unstyled mb-0">
                    <li v-for="account in result.accounts" :key="account.userId">
                        {{ account.userName }} — {{ account.companyName || 'no company' }}
                        <small class="text-muted">(stored as {{ account.phoneNumberAsStored }})</small>
                    </li>
                </ul>
                <p v-else class="mb-0 text-muted">No GreaseBook account holds this number.</p>
            </div>

            <h6 class="text-muted">Recent messages</h6>
            <table v-if="result.messages.length" class="table table-sm table-hover align-middle">
                <thead>
                    <tr>
                        <th style="width: 14rem">Date (UTC)</th>
                        <th>Message</th>
                        <th style="width: 24rem">Status</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(message, index) in result.messages" :key="`sms-${index}`">
                        <td>{{ formatDate(message.sentDate) }}</td>
                        <td :title="message.body">
                            <div>
                                <i v-if="isInbound(message)" class="bi bi-reply text-muted me-1" title="They sent us this"></i>
                                {{ parseBody(message).subject }}
                                <!-- Not a link: opening it would count as the customer's click on that alert. -->
                                <i v-if="parseBody(message).url" class="bi bi-link-45deg text-muted" :title="parseBody(message).url"></i>
                            </div>
                            <div v-if="parseBody(message).detail" class="text-muted small">{{ parseBody(message).detail }}</div>
                        </td>
                        <td>
                            <span class="badge" :class="getStatus(message).css">{{ getStatus(message).text }}</span>
                            <div v-if="getFailureReason(message)" class="small text-danger mt-1">
                                {{ getFailureReason(message) }}
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
            <p v-else class="text-muted">No texts to or from this number in the period our provider still holds.</p>
        </div>

        <Spinner :show="isLoading" />
    </div>
</template>
